import React, { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Target, Star, TrendingUp, Award, ChartLine,
  Calendar, Plus, Download, Filter, CheckCircle2,
  Clock, AlertTriangle, MessageSquare, FileText,
  BarChart2, ArrowUpRight, Users, ChevronRight
} from 'lucide-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  Radar
} from 'recharts';
import { candidatesData } from '../data/mockData';
import NewReviewForm from './NewReviewForm';
import ReviewDetails from './ReviewDetails';

const PerformanceCard = ({ review }) => {
  const [showDetails, setShowDetails] = useState(false);

  const handleClose = useCallback(() => {
    setShowDetails(false);
  }, []);

  const handleViewClick = useCallback((e) => {
    e.stopPropagation();
    setShowDetails(true);
  }, []);

  const getScoreColor = (score) => {
    if (score >= 4.5) return 'text-green-600';
    if (score >= 4.0) return 'text-blue-600';
    if (score >= 3.0) return 'text-amber-600';
    return 'text-red-600';
  };

  const averageScore = Object.values(review.scores).reduce((a, b) => a + b, 0) / 
    Object.values(review.scores).length;

  return (
    <motion.div
      whileHover={{ scale: 1.01 }}
      className="bg-white rounded-xl p-6 shadow-sm"
    >
      <div className="flex justify-between items-start mb-6">
        <div>
          <h3 className="font-semibold text-gray-900">{review.employeeName}</h3>
          <p className="text-sm text-gray-500">Review Period: {review.period}</p>
        </div>
        <div className={`text-2xl font-bold ${getScoreColor(averageScore)}`}>
          {averageScore.toFixed(1)}
        </div>
      </div>

      {/* Performance Metrics */}
      <div className="space-y-3 mb-6">
        {Object.entries(review.scores).map(([metric, score]) => (
          <div key={metric}>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-600 capitalize">
                {metric.replace(/([A-Z])/g, ' $1').trim()}
              </span>
              <span className={`font-medium ${getScoreColor(score)}`}>
                {score.toFixed(1)}
              </span>
            </div>
            <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`h-full ${score >= 4.5 ? 'bg-green-500' : 
                  score >= 4.0 ? 'bg-blue-500' :
                  score >= 3.0 ? 'bg-amber-500' :
                  'bg-red-500'}`}
                style={{ width: `${(score / 5) * 100}%` }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Goals */}
      <div className="border-t pt-4">
        <h4 className="text-sm font-medium text-gray-900 mb-3">Goals</h4>
        <div className="space-y-2">
          {review.goals.map((goal, index) => (
            <div key={index} className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${
                  goal.status === 'Completed' ? 'bg-green-500' :
                  goal.status === 'In Progress' ? 'bg-blue-500' :
                  'bg-amber-500'
                }`} />
                <span className="text-gray-600">{goal.title}</span>
              </div>
              <span className="text-gray-500">{goal.deadline}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-2 mt-4 pt-4 border-t">
        <button 
          onClick={handleViewClick}
          className="flex-1 py-2 text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100 
            transition-colors text-sm font-medium"
        >
          View Full Review
        </button>
        <button className="flex-1 py-2 text-gray-600 bg-gray-50 rounded-lg hover:bg-gray-100 
          transition-colors text-sm font-medium">
          Schedule Follow-up
        </button>
      </div>

      {showDetails && (
        <ReviewDetails
          review={review}
          onClose={handleClose}
          onEdit={(review) => {
            console.log('Edit review:', review);
            handleClose();
          }}
        />
      )}
    </motion.div>
  );
};

const TeamPerformanceCard = ({ team }) => {
  return (
    <div className="bg-white rounded-xl p-6 shadow-sm">
      <div className="flex justify-between items-start mb-4">
        <div>
          <h3 className="font-semibold text-gray-900">{team.name}</h3>
          <p className="text-sm text-gray-500">{team.members} team members</p>
        </div>
        <div className="text-2xl font-bold text-indigo-600">
          {team.score.toFixed(1)}
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-gray-500">Performance Score</span>
          <span className="font-medium">{(team.score * 20).toFixed(0)}%</span>
        </div>
        <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-indigo-600"
            style={{ width: `${team.score * 20}%` }}
          />
        </div>
      </div>

      <div className="mt-4 pt-4 border-t grid grid-cols-2 gap-4 text-sm">
        <div>
          <span className="text-gray-500">Reviews Completed</span>
          <div className="font-medium text-gray-900">{team.reviewsCompleted}/{team.totalReviews}</div>
        </div>
        <div>
          <span className="text-gray-500">Goals Achieved</span>
          <div className="font-medium text-gray-900">{team.goalsAchieved}%</div>
        </div>
      </div>
    </div>
  );
};

const PerformanceModule = ({ isMobile }) => {
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [filterPeriod, setFilterPeriod] = useState('current');
  const [showAddReview, setShowAddReview] = useState(false);

  const teamPerformance = [
    {
      name: 'Engineering',
      members: 12,
      score: 4.5,
      reviewsCompleted: 10,
      totalReviews: 12,
      goalsAchieved: 85
    },
    {
      name: 'Sales',
      members: 8,
      score: 4.2,
      reviewsCompleted: 7,
      totalReviews: 8,
      goalsAchieved: 92
    },
    {
      name: 'Marketing',
      members: 6,
      score: 4.3,
      reviewsCompleted: 6,
      totalReviews: 6,
      goalsAchieved: 88
    }
  ];

  const handleAddReview = (reviewData) => {
    console.log('New review:', reviewData);
    setShowAddReview(false);
  };

  return (
    <div className="space-y-6">
      {/* Header Actions */}
      <div className="flex flex-wrap gap-4 items-center justify-between">
        <div className="flex items-center gap-4">
          <select
            value={filterDepartment}
            onChange={(e) => setFilterDepartment(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500"
          >
            <option value="all">All Departments</option>
            <option value="engineering">Engineering</option>
            <option value="sales">Sales</option>
            <option value="marketing">Marketing</option>
          </select>
          <select
            value={filterPeriod}
            onChange={(e) => setFilterPeriod(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500"
          >
            <option value="current">Current Quarter</option>
            <option value="last">Last Quarter</option>
            <option value="year">This Year</option>
          </select>
        </div>
        <div className="flex gap-2">
          <button className="px-4 py-2 border border-gray-200 rounded-xl hover:bg-gray-50 
            flex items-center gap-2">
            <Download className="w-4 h-4" />
            Export Report
          </button>
          <button 
            onClick={() => setShowAddReview(true)}
            className="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 
              flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            New Review
          </button>
        </div>
      </div>

      {/* Team Performance Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {teamPerformance.map((team, index) => (
          <TeamPerformanceCard key={index} team={team} />
        ))}
      </div>

      {/* Individual Reviews */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">Recent Reviews</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {candidatesData.performanceReviews.map((review, index) => (
            <PerformanceCard key={index} review={review} />
          ))}
        </div>
      </div>

      {/* Performance Analytics */}
      <div className="bg-white rounded-xl p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Performance Trends</h3>
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart data={[
              {
                subject: 'Technical',
                score: 4.5,
                fullMark: 5,
              },
              {
                subject: 'Communication',
                score: 4.2,
                fullMark: 5,
              },
              {
                subject: 'Leadership',
                score: 4.0,
                fullMark: 5,
              },
              {
                subject: 'Innovation',
                score: 4.3,
                fullMark: 5,
              },
              {
                subject: 'Teamwork',
                score: 4.8,
                fullMark: 5,
              },
            ]}>
              <PolarGrid />
              <PolarAngleAxis dataKey="subject" />
              <Radar
                name="Performance"
                dataKey="score"
                stroke="#4F46E5"
                fill="#4F46E5"
                fillOpacity={0.2}
              />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {showAddReview && (
        <NewReviewForm
          onClose={() => setShowAddReview(false)}
          onSubmit={handleAddReview}
        />
      )}
    </div>
  );
};

export default PerformanceModule; 